import type { QueryClient } from '@tanstack/react-query';
import { apiGetBillingOverview, type BillingOverview, type SubscriptionTier } from '~/lib/api/billing';
import { setLiveCredits } from '~/lib/stores/credits';
import { $accessToken, $currentUser, setAuth } from '~/lib/stores/auth';

// Every react-query key that reflects credit / plan / billing state, so one purchase
// refreshes them all where they're being observed (the billing tabs).
const BILLING_QUERY_KEYS = [
  ['credit-balance'],
  ['credit-history'],
  ['credit-purchases'],
  ['invoices'],
  // Plus/Pro (the desktop-family plan routes): overview, invoices, top-up rate
  ['account-plan-overview'],
  ['account-plan-invoices'],
  ['account-plan-topup'],
] as const;

/**
 * Fetch the freshest billing state once and fan it out to EVERY place credits/plan
 * are shown, so they all update without a page refresh:
 *  - the `billing-overview` react-query cache (billing tabs, even when re-opened),
 *  - the `$liveCredits` store (the sidebar footer + profile-menu balance),
 *  - the auth user's `tier` (the sidebar plan label, and anywhere reading user.tier).
 *
 * The sidebar reads stores rather than react-query, so pushing into the stores is what
 * makes the always-visible balance/plan update live. Best-effort: a failed tick is
 * retried by the poller.
 */
export async function syncBillingState(queryClient: QueryClient): Promise<BillingOverview | null> {
  try {
    const overview = await apiGetBillingOverview();
    queryClient.setQueryData(['billing-overview'], overview);

    if (typeof overview.creditsRemaining === 'number') {
      setLiveCredits(overview.creditsRemaining);
    }

    const token = $accessToken.get();
    const user = $currentUser.get();
    const tier = overview.activePlan ?? overview.tier;

    if (token && user && tier && user.tier !== tier) {
      setAuth(token, { ...user, tier });
    }

    for (const key of BILLING_QUERY_KEYS) {
      queryClient.invalidateQueries({ queryKey: [...key] });
    }

    return overview;
  } catch {
    // Best-effort — the async webhook grant may not have landed yet; the next tick retries.
    return null;
  }
}

/**
 * A paid plan is active on the account: a website plan, or Plus/Pro (which
 * lives on the desktop plan columns, so the website tier stays 'free').
 */
export function isPlanLive(overview: BillingOverview): boolean {
  const product = overview.accountPlan?.product;

  return (overview.status === 'active' && overview.tier !== 'free') || product === 'code' || product === 'work';
}

/**
 * Entitlements are granted server-side by webhooks a few seconds AFTER a purchase, so a
 * single sync races the grant. Poll `syncBillingState` on a bounded backoff until the
 * new state converges. Returns a cleanup that cancels pending ticks.
 *
 * `onSubscribed` fires AT MOST ONCE, the first tick that observes an active paid plan —
 * used to trigger the welcome celebration only after the plan genuinely goes live.
 */
export function pollBillingState(
  queryClient: QueryClient,
  opts?: { onSubscribed?: (overview: BillingOverview) => void },
): () => void {
  const timers: number[] = [];
  let announced = false;

  const tick = async () => {
    const overview = await syncBillingState(queryClient);

    if (!announced && overview && isPlanLive(overview)) {
      announced = true;
      opts?.onSubscribed?.(overview);
    }
  };

  void tick();

  // Dense early ticks catch a fast webhook within ~1s; the long tail (out to ~35s)
  // still converges when a webhook is slow, so nothing is left stale.
  for (const delay of [800, 1600, 2600, 4000, 6000, 9000, 13000, 18000, 25000, 35000]) {
    timers.push(window.setTimeout(() => void tick(), delay));
  }

  return () => {
    for (const t of timers) {
      window.clearTimeout(t);
    }
  };
}

/**
 * Instantly reflect a just-confirmed plan change everywhere the sidebar reads from —
 * the live-credit balance and the auth user's tier — WITHOUT waiting for the webhook
 * poll. Purely optimistic; the poll's real server state overwrites it moments later.
 */
export function optimisticPlanUpdate(tier: SubscriptionTier, credits: number): void {
  setLiveCredits(credits);

  const token = $accessToken.get();
  const user = $currentUser.get();

  if (token && user && user.tier !== tier) {
    setAuth(token, { ...user, tier });
  }
}
